import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatDuration } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Barbell, VideoCamera, Star } from "@phosphor-icons/react";

/**
 * Smart autocomplete for movement names.
 *
 * Pools three sources:
 *   1. Coach's personal exercise library (`/api/exercises`)
 *   2. The static CrossFit movement library (`/api/movement-library`)
 *   3. (Lazy-fetched, deduped by name.)
 *
 * Each match shows a rich row: name + category chip + the movement's default
 * reps/weight/distance/cal so the coach can confirm at a glance, plus a video
 * icon if the movement has a demo link.
 *
 * On pick, calls `onPickFull({name, sets, reps, weight, weight_unit, ...})`
 * with normalized defaults so the parent form can pre-fill its fields.
 * If the parent only needs the name back, it can ignore the extra fields.
 */
export default function MovementAutocomplete({
  value, onChange, onPickFull,
  placeholder = "Movement name…",
  disabled,
  testId = "movement-name-input",
  className = "",
  required,
}) {
  const [open, setOpen] = useState(false);
  const [mine, setMine] = useState([]);
  const [cf, setCf] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  // Lazy fetch when user starts typing.
  useEffect(() => {
    if (!value || value.length < 1) return;
    if (mine.length === 0) {
      api.get("/exercises").then((r) => setMine(r.data)).catch(() => {});
    }
    if (cf.length === 0) {
      api.get("/movement-library").then((r) => setCf(r.data)).catch(() => {});
    }
  }, [value, mine.length, cf.length]);

  // Close on click-outside.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const matches = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return [];
    const norm = (ex, source) => ({
      key: `${source}:${ex.id || ex.name}`,
      source,                                            // "mine" | "cf"
      name: ex.name,
      category: ex.category || (source === "mine" ? "Saved" : null),
      reps: ex.reps ?? ex.default_reps,
      weight: ex.weight ?? ex.default_weight,
      weight_unit: ex.weight_unit || "lb",
      time_seconds: ex.time_seconds ?? ex.default_time_seconds,
      distance_meters: ex.distance_meters ?? ex.default_distance_meters,
      calories: ex.calories ?? ex.default_calories,
      video_url: ex.video_url,
      raw: ex,
    });
    const matchers = (txt) => (txt || "").toLowerCase().includes(q);
    const m1 = mine.filter((ex) => matchers(ex.name)).map((ex) => norm(ex, "mine"));
    const m2 = cf.filter((ex) => matchers(ex.name)).map((ex) => norm(ex, "cf"));
    // Dedupe by name, preferring coach's saved variant.
    const seen = new Set();
    const all = [...m1, ...m2].filter((it) => {
      const k = it.name.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k); return true;
    });
    return all.slice(0, 8);
  }, [value, mine, cf]);

  useEffect(() => { setHighlight(0); }, [matches.length]);

  const pick = (it) => {
    setOpen(false);
    onChange(it.name);
    if (onPickFull) {
      onPickFull({
        source: it.source,     // "mine" (Gym Library) | "cf" (CrossFit Library)
        name: it.name,
        sets: it.raw.sets ?? 3,
        reps: it.reps ?? null,
        weight: it.weight ?? null,
        weight_unit: it.weight_unit || "lb",
        time_seconds: it.time_seconds ?? null,
        distance_meters: it.distance_meters ?? null,
        distance_unit: it.raw.distance_unit || "m",
        height_inches: it.raw.height_inches ?? null,
        height_unit: it.raw.height_unit || "in",
        calories: it.calories ?? null,
        rpe: it.raw.rpe ?? null,
        notes: it.raw.notes || "",
        video_url: it.video_url || "",
        form_notes: it.raw.form_notes || "",
        body_part: it.raw.body_part ?? null,
        primary_muscle: it.raw.primary_muscle ?? null,
      });
    }
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % matches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + matches.length) % matches.length); }
    else if (e.key === "Enter") {
      // Prefer Tab/Click for picking, but Enter on a highlighted suggestion is friendly.
      if (matches[highlight] && (e.target.value || "").toLowerCase() !== matches[highlight].name.toLowerCase()) {
        e.preventDefault();
        pick(matches[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <Input
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        required={required}
        autoComplete="off"
        className="h-11 rounded-xl"
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 bg-surface border border-line rounded-2xl shadow-lift overflow-hidden"
          data-testid="movement-autocomplete-results"
        >
          <ul className="max-h-72 overflow-y-auto py-1">
            {matches.map((it, idx) => (
              <li key={it.key}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(it)}
                  onMouseEnter={() => setHighlight(idx)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                    idx === highlight ? "bg-sage" : "hover:bg-muted2"
                  }`}
                  data-testid={`movement-autocomplete-item-${idx}`}
                >
                  <Barbell size={16} className="text-moss shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-ink truncate">{it.name}</span>
                      {it.source === "mine" && (
                        <Star size={12} weight="fill" className="text-terracotta shrink-0" title="Your saved library" />
                      )}
                      {it.category && (
                        <span className="text-[10px] uppercase tracking-[0.12em] text-ink-3 shrink-0">
                          {it.category}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-data text-ink-3 mt-0.5 truncate">
                      {[
                        it.reps && `${it.reps} reps`,
                        it.weight && `@ ${it.weight} ${it.weight_unit || "lb"}`,
                        it.time_seconds && formatDuration(it.time_seconds),
                        it.distance_meters && `${it.distance_meters}m`,
                        it.calories && `${it.calories} cal`,
                      ].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  {it.video_url && (
                    <VideoCamera size={14} className="text-ink-3 shrink-0" title="Demo video available" />
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="text-[10px] uppercase tracking-[0.14em] text-ink-3 px-3 py-1.5 border-t border-line bg-muted2">
            ↑/↓ to navigate · Enter to pick · Esc to dismiss
          </div>
        </div>
      )}
    </div>
  );
}
